// dashboard/patientList/QuickActions.js
// The "⋯" quick-actions dropdown shown on a PetCard header. Extracted from
// App.js (2026-08-12 split).

import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';
import { c } from '../constants';

export default function QuickActions({ patient, onSelect, onDismissAlert, onPrintSummary, vets = [], canClinical, onReassignDoctor, onAssignCondition }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [subView, setSubView] = useState(null); // null | 'assignDoctor'
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const ref = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = `quick-actions-${patient?.pet?.id || patient?.condition?.id || 'patient'}`;

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSubView(null); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) menuRef.current?.querySelector('[role="menuitem"]:not(:disabled)')?.focus();
  }, [open, subView]);

  const closeMenu = (restoreFocus = false) => {
    setOpen(false);
    setSubView(null);
    if (restoreFocus) btnRef.current?.focus();
  };

  const handleMenuKeyDown = e => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu(true);
      return;
    }
    if (e.key === 'Tab') {
      closeMenu(false);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const items = [...menuRef.current.querySelectorAll('[role="menuitem"]:not(:disabled)')];
    if (!items.length) return;
    const current = items.indexOf(document.activeElement);
    const next = e.key === 'Home' ? 0
      : e.key === 'End' ? items.length - 1
        : e.key === 'ArrowDown' ? (current + 1 + items.length) % items.length
          : (current - 1 + items.length) % items.length;
    items[next].focus();
  };

  const actions = [
    { label: t('quickActions.reviewPatient'),   action: () => onSelect(patient) },
    { label: t('quickActions.markReviewed'),    action: () => patient.alert && onDismissAlert(patient.alert.id), disabled: !patient.alert },
    { label: '─', divider: true },
    { label: t('quickActions.printSummary'),    action: () => onPrintSummary(patient) },
    // Previously only reachable from the detail panel's own dropdown, buried
    // a click deeper than the card-level "⋯" a clinician actually reaches
    // for first (Bryan punch list item 4, 2026-09-16). Same onReassignDoctor/
    // onAssignCondition callbacks and canClinical gate as
    // PatientDetailPanel.js's equivalent menu — Manager accounts excluded
    // from clinical actions (Bryan, 2026-08-06).
    { label: '─', divider: true },
    { label: t('patientDetail.assignDoctor'), action: () => setSubView('assignDoctor'), noClose: true },
    ...(canClinical ? [
      { label: t('patientDetail.assignCondition'), action: () => onAssignCondition(patient) },
    ] : []),
  ];

  // This menu is `position: fixed`, always anchored below the button with
  // no viewport-edge check — on a card in the lower part of a long roster
  // it can render partly or fully off-screen, and being `fixed` (not
  // inside a scrollable ancestor) means scrolling the page can't bring it
  // back into view. Same bug class as the sidebar's "My default view"
  // popover, fixed there with an explicit openUpward prop — here there's
  // no fixed "always near the bottom" caller to hang that off of, so the
  // direction is decided per-open instead, from the button's own position.
  // Estimated rather than measured (no two-pass render needed) — a row is
  // ~37px (matches the button's own '9px 14px' padding + ~13px line
  // height), a divider ~5px; self-adjusts if `actions` ever grows.
  const handleOpen = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const estimatedHeight = actions.reduce((h, a) => h + (a.divider ? 5 : 37), 0);
      const opensUpward = (window.innerHeight - rect.bottom) < (estimatedHeight + 8) && rect.top > estimatedHeight;
      setMenuPos(opensUpward
        ? { bottom: window.innerHeight - rect.top + 4, right: window.innerWidth - rect.right }
        : { top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen(o => !o);
    setSubView(null);
  };

  return (
    <div ref={ref} style={{ display: 'inline-block' }}>
      <button
        ref={btnRef}
        onClick={handleOpen}
        onKeyDown={e => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!open) handleOpen(e);
          }
        }}
        style={{
          padding: '6px 10px',
          border: `1px solid ${c.border}`,
          borderRadius: 6,
          background: 'none',
          cursor: 'pointer',
          fontSize: 14,
          color: c.textSoft,
          lineHeight: 1,
        }}
        aria-label={t('quickActions.ariaLabel')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
      >
        ⋯
      </button>
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={t('quickActions.ariaLabel')}
          onKeyDown={handleMenuKeyDown}
          style={{
          position: 'fixed',
          ...menuPos,
          backgroundColor: c.white,
          border: `1px solid ${c.border}`,
          borderRadius: 10,
          boxShadow: '0 4px 16px rgba(79,100,115,0.14)',
          zIndex: 9999,
          minWidth: 160,
          overflow: 'hidden',
          }}
        >
          {subView === 'assignDoctor' ? (
            <>
              <button
                role="menuitem"
                onClick={e => { e.stopPropagation(); setSubView(null); }}
                style={{
                  display: 'block', width: '100%', padding: '9px 14px', textAlign: 'left',
                  background: c.fog, border: 'none', borderBottom: `1px solid ${c.border}`,
                  fontSize: 12, color: c.textSoft, cursor: 'pointer',
                }}
              >
                {t('patientDetail.back')}
              </button>
              {vets.length === 0 && (
                <div style={{ padding: '9px 14px', fontSize: 12, color: c.textSoft }}>{t('patientDetail.noDoctorsOnFile')}</div>
              )}
              {vets.map(v => {
                const isCurrent = patient.assignedVet?.id === v.id;
                return (
                  <button
                    key={v.id}
                    role="menuitem"
                    onClick={e => { e.stopPropagation(); onReassignDoctor(patient.condition?.id, v.id); closeMenu(true); }}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      width: '100%', padding: '9px 14px', textAlign: 'left',
                      background: isCurrent ? c.fog : 'none', border: 'none',
                      fontSize: 13, color: c.slate, cursor: 'pointer',
                    }}
                  >
                    <span>{t('patientDetail.doctorPrefix', { firstName: v.first_name, lastName: v.last_name })}</span>
                    {isCurrent && <span style={{ fontSize: 11, color: c.moss }}>✓</span>}
                  </button>
                );
              })}
            </>
          ) : actions.map((a, i) => a.divider ? (
            <div key={i} role="separator" style={{ height: 1, backgroundColor: c.border, margin: '2px 0' }} />
          ) : (
            <button
              key={i}
              role="menuitem"
              disabled={a.disabled}
              onClick={e => { e.stopPropagation(); if (!a.disabled) { a.action(); if (!a.noClose) closeMenu(true); } }}
              style={{
                display: 'block',
                width: '100%',
                padding: '9px 14px',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                fontSize: 13,
                color: a.disabled ? '#ccc' : c.slate,
                cursor: a.disabled ? 'default' : 'pointer',
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
